import { Box, Button, FormControl, Select, MenuItem } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
export default function Pagination({
  totalPages = 10,
  currentPage = 1,
  onPageChange,
  pageSizeOptions = [10, 20, 50, 100],
  pageSize = 10,
  onPageSizeChange
}) {
  const [selectedPageSize, setSelectedPageSize] = useState(pageSize);
  const { t } = useTranslation();
  const handlePageSizeChange = (event) => {
    const newSize = event.target.value;
    setSelectedPageSize(newSize);
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }
  };

  const handlePageChange = (page) => {
    if (onPageChange && page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  // Generate page numbers with "1 2 ... 9 10" format
  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first two pages
      pages.push(1);
      pages.push(2);

      // Add ellipsis if current page is not near the beginning
      if (currentPage > 4) {
        pages.push('...');
      }

      // Show current page if it's not in the first two or last two
      if (currentPage > 2 && currentPage < totalPages - 1) {
        pages.push(currentPage);
      }

      // Add ellipsis if current page is not near the end
      if (currentPage < totalPages - 3) {
        pages.push('...');
      }

      // Always show last two pages
      if (totalPages > 1) {
        pages.push(totalPages - 1);
        pages.push(totalPages);
      }
    }

    // Remove duplicates and ellipsis next to each other
    return pages.filter((page, index, arr) => {
      // Remove duplicate ellipsis
      if (page === '...' && arr[index - 1] === '...') {
        return false;
      }
      // Remove duplicate numbers
      if (typeof page === 'number' && page === arr[index - 1]) {
        return false;
      }
      return true;
    });
  };

  return (
    <Box sx={{
      position: 'relative',
      display: 'flex',
      flexDirection: {
        xs: 'column', // Stack vertically on extra small screens
        sm: 'row'     // Horizontal layout on small and larger screens
      },
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      mt: 2,
      zIndex: 20,
      borderRadius: "4px",
      padding: {
        xs: "12px", // Less padding on small screens
        sm: "12px 24px"
      },
      gap: {
        xs: 2,      // Add gap when stacked vertically
        sm: 0       // No gap in horizontal layout
      },
      overflow: 'visible'
    }}>
      {/* Page size selector */}
      <FormControl
        sx={{
          minWidth: 120,
          width: {
            xs: '100%',  // Full width on small screens
            sm: 'auto'   // Auto width on larger screens
          }
        }}
      >
        <Select
          labelId="page-size-label"
          id="page-size-select"
          value={selectedPageSize}
          onChange={handlePageSizeChange}
          size="small"
          fullWidth
        >
          {pageSizeOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option} {t("Rows")}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Page number indicators */}
      <Box sx={{
        display: {
          xs: 'none',  // Hide on extra small screens
          sm: 'flex'   // Show on small and larger screens
        },
        alignItems: 'center',
        width: 209,
        height: 43,
        gap: 2,
        overflow: "visible"
      }}>
        {getPageNumbers().map((page, index) => (
          <Box
            key={index}
            sx={{
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: page === '...' ? 'default' : 'pointer',
              borderRadius: '4px',
              backgroundColor: page === currentPage ? '#647490' : 'transparent',
              color: page === currentPage ? 'white' : 'text.primary',
              padding: '8px 12px',
              '&:hover': {
                backgroundColor: page === '...' ? 'transparent' : 'action.hover'
              }
            }}
            onClick={() => page !== '...' && handlePageChange(page)}
          >
            {page}
          </Box>
        ))}
      </Box>

      {/* Navigation buttons */}
      <Box sx={{
        display: 'flex',
        flexDirection: {
          xs: 'column', // Stack vertically on extra small screens
          sm: 'row'     // Horizontal layout on small and larger screens
        },
        gap: 2,
        width: {
          xs: '100%',   // Full width on small screens
          sm: 238       // Fixed width on larger screens
        },
        height: {
          xs: 'auto',   // Auto height when stacked
          sm: 43        // Fixed height in horizontal layout
        }
      }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          sx={{
            borderRadius: 1,
            background: "#F2F2F3",
            border: '1px solid #414958',
            padding: '12px 24px',
            height: {
              xs: 40,   // Slightly smaller on small screens
              sm: 43    // Standard height on larger screens
            },
            display: 'flex',
            gap: "10px",
            alignItems: 'center'
          }}
        >
          <span>&lt;</span> <span>{t("Previous")}</span>
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          sx={{
            borderRadius: 1,
            padding: '12px 24px',
            backgroundColor: '#20A8E0',
            color: '#FFFFFF',
            height: {
              xs: 40,   // Slightly smaller on small screens
              sm: 43    // Standard height on larger screens
            },
            display: 'flex',
            gap: "10px",
            alignItems: 'center',
            '&:hover': {
              backgroundColor: '#1897cc'
            }
          }}
        >
          <span>{t("Next")}</span> <span>&gt;</span>
        </Button>
      </Box>
    </Box>
  );
}